import React, { useContext, useState } from "react";
import {
  Avatar, Box, Menu, MenuItem, Typography, Divider, IconButton,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import UserContext from "./UserContext";
import { useNavigate } from "react-router-dom";

const BRAND = { navy: "#0a0e27", accent: "#637cdb" };

export default function NavUserMenu() {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const initials = user?.name
    ? user.name.split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "U";

  const handleLogout = () => {
    setAnchor(null);
    setUser(null);
    navigate("/", { replace: true });
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        size="small"
        sx={{ p: 0, ml: 0.5 }}
      >
        <Avatar
          sx={{
            width: 40, height: 40,
            bgcolor: BRAND.accent,
            color: BRAND.navy,
            fontSize: 14,
            fontWeight: 800,
            border: "2px solid rgba(255,255,255,0.25)",
            transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
            "&:hover": { borderColor: BRAND.accent, transform: "scale(1.06)", boxShadow: "0 0 0 3px rgba(99,124,219,0.3)" },
            cursor: "pointer",
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 230,
              borderRadius: "14px",
              border: "1px solid rgba(10,14,39,0.1)",
              boxShadow: "0 12px 48px rgba(10,14,39,0.14), 0 2px 8px rgba(0,0,0,0.06)",
              mt: 1.25,
              overflow: "visible",
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: -1, right: 14,
                width: 10, height: 10,
                bgcolor: "background.paper",
                border: "1px solid rgba(10,14,39,0.1)",
                borderRight: "none",
                borderBottom: "none",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: BRAND.navy, fontSize: 14, fontWeight: 800 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.navy, lineHeight: 1.3 }}>
              {user?.name || "User"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.15 }}>
              {user?.email || ""}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mx: 2 }} />

        <Box sx={{ px: 1.5, py: 1 }}>
          <MenuItem
            onClick={() => { setAnchor(null); navigate("/home"); }}
            sx={{
              gap: 1.5, py: 1, px: 1.5, borderRadius: "8px",
              color: "#475569", fontSize: 13, fontWeight: 500,
              "&:hover": { bgcolor: "rgba(10,14,39,0.06)", color: BRAND.navy },
            }}
          >
            <PersonIcon sx={{ fontSize: 16 }} />
            My Dashboard
          </MenuItem>

          {user?.role !== "clinician" && (
            <MenuItem
              onClick={() => { setAnchor(null); navigate("/my-payments"); }}
              sx={{
                gap: 1.5, py: 1, px: 1.5, borderRadius: "8px",
                color: "#475569", fontSize: 13, fontWeight: 500,
                "&:hover": { bgcolor: "rgba(10,14,39,0.06)", color: BRAND.navy },
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 16 }} />
              My Payments
            </MenuItem>
          )}

          <MenuItem
            onClick={handleLogout}
            sx={{
              gap: 1.5, py: 1, px: 1.5, borderRadius: "8px",
              color: "#ef4444", fontSize: 13, fontWeight: 600,
              "&:hover": { bgcolor: "rgba(239,68,68,0.07)" },
            }}
          >
            <LogoutIcon sx={{ fontSize: 16 }} />
            Sign out
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
}
